import {useEffect, useState} from 'react';
import {useAtom} from 'jotai';
import {toast} from 'sonner';
import {WifiOff, Wifi, LogOut, Sun, Moon, Monitor} from 'lucide-react';
import {
  offlineModeAtom,
  offlineBannerHiddenAtom,
  themeModeAtom,
} from '@lib/atoms';
import type {ThemeMode} from '@lib/theme';
import {cn} from '@lib/utils';
import {Button} from '@ui/shadcn/button';
import {ToggleGroup, ToggleGroupItem} from '@ui/shadcn/toggle-group';
import {useAuth} from '@shared/api/auth';
import {useActiveWorkspace} from '@api/workspaces';
import {DemoPersonaSwitcher} from '@features/demo-persona';

const THEME_MODE_ITEMS: {
  value: ThemeMode;
  label: string;
  icon: React.ReactNode;
}[] = [
  {value: 'light', label: 'Светлая', icon: <Sun size={14} />},
  {value: 'dark', label: 'Тёмная', icon: <Moon size={14} />},
  {value: 'system', label: 'Системная', icon: <Monitor size={14} />},
];

interface ProfileMenuProps {
  /** Координаты якоря (нижний левый угол аватарки) — для позиционирования popover'а. */
  anchor: {left: number; bottom: number};
  onClose: () => void;
}

const LogoutConfirm = ({
  onCancel,
  onConfirm,
}: {
  onCancel: () => void;
  onConfirm: () => void;
}) => (
  <div
    className='fixed inset-0 bg-black/45 flex items-center justify-center px-4'
    style={{zIndex: 'var(--z-modal-task)'}}
    onClick={(e) => {
      if (e.target === e.currentTarget) onCancel();
    }}
  >
    <div className='w-full max-w-[380px] bg-[var(--color-surface)] rounded-xl shadow-2xl overflow-hidden'>
      <div className='px-5 py-4'>
        <h3 className='text-sm font-semibold mb-1.5'>Выйти из системы?</h3>
        <p className='text-xs text-[var(--color-muted-foreground)] leading-relaxed'>
          Демо-стенд без авторизации: после выхода вы вернётесь в систему под
          демо-администратором.
        </p>
      </div>
      <div className='flex items-center justify-end gap-2 px-5 py-3 bg-[var(--color-muted)] border-t border-[var(--color-border)]'>
        <Button
          type='button'
          variant='outline'
          size='sm'
          onClick={onCancel}
          className='text-[var(--color-secondary)]'
        >
          Отмена
        </Button>
        <Button
          type='button'
          variant='destructive'
          size='sm'
          onClick={onConfirm}
        >
          Выйти
        </Button>
      </div>
    </div>
  </div>
);

/** Popover-меню профиля (раунд 16 §2). «Поддержка» вынесена в иконку «?» сайдбара. */
export const ProfileMenu = ({anchor, onClose}: ProfileMenuProps) => {
  const [offline, setOffline] = useAtom(offlineModeAtom);
  const [, setBannerHidden] = useAtom(offlineBannerHiddenAtom);
  const [themeMode, setThemeMode] = useAtom(themeModeAtom);
  const {currentUser, logout} = useAuth();
  const {active: activeWorkspace} = useActiveWorkspace();
  // Демо-режим «клиент» отвязан от backend (ролей больше нет — только действия).
  const isClient = false;

  const [logoutOpen, setLogoutOpen] = useState(false);

  // Закрытие по Esc
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const toggleOffline = () => {
    if (offline) {
      setOffline(false);
      setBannerHidden(false);
      toast.success('Соединение восстановлено', {duration: 2000});
    } else {
      setOffline(true);
      setBannerHidden(false);
      toast.error('Demo: режим без интернета включён', {duration: 2000});
    }
    onClose();
  };

  const handleLogout = () => {
    setLogoutOpen(false);
    onClose();
    logout();
  };

  const displayName = currentUser?.displayName ?? currentUser?.email ?? 'Гость';
  const displaySubtitle = (() => {
    // Реальные права видны в «Команде»; здесь — только оператор/воркспейс.
    const roleLabel = currentUser?.isOperator ? 'Оператор платформы' : null;
    const orgName = activeWorkspace?.name;
    if (roleLabel && orgName) return `${roleLabel} · ${orgName}`;
    return roleLabel ?? orgName ?? currentUser?.email ?? '';
  })();

  const items: {
    icon: React.ReactNode;
    label: string;
    onClick: () => void;
    danger?: boolean;
  }[] = [
    {
      icon: <LogOut size={14} />,
      label: 'Выйти',
      onClick: () => setLogoutOpen(true),
      danger: true,
    },
  ];

  return (
    <>
      {/* Click-outside catcher */}
      <div
        className='fixed inset-0'
        style={{zIndex: 'calc(var(--z-dropdown) - 1)'}}
        onClick={onClose}
      />

      {/* Popover */}
      <div
        className='fixed w-[240px] bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow-lg overflow-hidden'
        style={{
          left: anchor.left,
          bottom: anchor.bottom,
          zIndex: 'var(--z-dropdown)',
        }}
      >
        {/* Profile header */}
        <div className='px-3 py-2.5'>
          <p className='text-sm font-semibold text-[var(--color-foreground)]'>
            {displayName}
          </p>
          <p className='text-[11px] text-[var(--color-muted-foreground)]'>
            {displaySubtitle}
          </p>
        </div>

        <div className='border-t border-[var(--color-border)]' />

        {/* Демо-стенд: переключение роли без авторизации */}
        <DemoPersonaSwitcher onSwitched={onClose} />

        <div className='border-t border-[var(--color-border)]' />

        {/* Тема оформления (личный выбор: светлая / тёмная / системная) */}
        <div className='px-3 py-2.5'>
          <p className='text-[11px] font-medium text-[var(--color-muted-foreground)] mb-1.5'>
            Тема оформления
          </p>
          <ToggleGroup
            type='single'
            value={themeMode}
            onValueChange={(v) => v && setThemeMode(v as ThemeMode)}
            variant='outline'
            spacing={0}
            className='w-full'
          >
            {THEME_MODE_ITEMS.map((item) => (
              <ToggleGroupItem
                key={item.value}
                value={item.value}
                aria-label={item.label}
                title={item.label}
                className='flex-1 gap-1.5 text-[11px]'
              >
                {item.icon}
                <span className='truncate'>{item.label}</span>
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>

        <div className='border-t border-[var(--color-border)]' />

        {/* Menu items */}
        <div className='py-1'>
          {items.map((item) => (
            <Button
              key={item.label}
              type='button'
              variant='ghost'
              onClick={item.onClick}
              className={cn(
                'w-full justify-start gap-2.5 px-3 h-9 rounded-none text-xs font-normal',
                item.danger
                  ? 'text-[var(--color-danger)] hover:bg-[var(--color-danger)]/10 hover:text-[var(--color-danger)]'
                  : 'text-[var(--color-foreground)] hover:bg-[var(--color-muted)]',
              )}
            >
              <span
                className={cn(
                  'shrink-0',
                  item.danger
                    ? 'text-[var(--color-danger)]'
                    : 'text-[var(--color-secondary)]',
                )}
              >
                {item.icon}
              </span>
              <span className='flex-1 truncate'>{item.label}</span>
            </Button>
          ))}
        </div>
      </div>

      {logoutOpen && (
        <LogoutConfirm
          onCancel={() => setLogoutOpen(false)}
          onConfirm={handleLogout}
        />
      )}
    </>
  );
};
